import { Link } from "wouter";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { apiRequest } from "@/lib/queryClient";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import logger from '@/lib/logger';
import { trackEvent } from "@/lib/analytics/posthog";
import { SEO } from "@/components/seo";

function FeatureItem({ included, children }: { included: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      {included ? (
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-500" />
      ) : (
        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-gray-400/70" />
      )}
      <span className={included ? "text-white/90" : "text-white/60"}>{children}</span>
    </li>
  );
}

export default function PricingPage() {
  const { user, isAuthenticated, isLoading, refreshUser } = useAuth();
  const { toast } = useToast();
  const currentTier = user?.planTier || "free";

  // Check if user is on a team (blocks upgrades)
  const { data: membershipData } = useQuery({
    queryKey: ['/api/player/team'],
    enabled: isAuthenticated && !!user?.id,
    retry: false,
  });
  const isOnTeam = !!(membershipData as any)?.team;
  const teamName = (membershipData as any)?.team?.name;

  // Helper to render a current plan badge above the matching card
  // Only show badge when auth is fully loaded and user is authenticated
  const renderCurrentPlanBadge = (tier: "free" | "pro_starter" | "elite_pro" | "ultra_team") => {
    if (isLoading || !isAuthenticated || currentTier !== tier) return null;
    return (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <Badge className="rounded-full bg-white text-black shadow-lg">Current Plan</Badge>
      </div>
    );
  };

  // Refresh user after returning from Stripe checkout success
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const status = params.get("status");
      if (status === "success" && isAuthenticated && user?.id) {
        // Poll for plan update with retry logic (webhook may take a moment to process)
        const maxRetries = 6;
        const retryDelay = 2000; // 2 seconds

        const pollForPlanUpdate = async (attempt = 1): Promise<boolean> => {
          try {
            const response = await apiRequest('GET', `/api/users/${user.id}`);
            const updatedUser = await response.json();

            if (updatedUser?.planTier && updatedUser.planTier !== 'free') {
              return true;
            }

            if (attempt < maxRetries) {
              await new Promise(r => setTimeout(r, retryDelay));
              return pollForPlanUpdate(attempt + 1);
            }
            return false;
          } catch (err) {
            logger.error("Error polling for plan update:", err as any);
            return false;
          }
        };

        pollForPlanUpdate().then((upgraded) => {
          refreshUser(); // Final refresh to update context
          trackEvent('checkout_completed', {
            previous_tier: currentTier,
            upgraded,
          });
          if (upgraded) {
            toast({
              title: "Upgrade successful!",
              description: "Your plan has been updated. Enjoy your new features!",
            });
          } else {
            toast({
              title: "Payment received",
              description: "Your payment was successful. If your plan doesn't update within a few minutes, please contact support.",
            });
          }
          // Remove status query param after showing toast
          const p = new URLSearchParams(window.location.search);
          p.delete("status");
          const newSearch = p.toString();
          const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}${window.location.hash}`;
          window.history.replaceState(null, "", newUrl);
        });
      } else if (status === "cancelled") {
        trackEvent('checkout_cancelled', { current_tier: currentTier });
        // Inform user that checkout did not complete
        toast({
          title: "Checkout cancelled",
          description: "Your checkout didn't complete. Your plan hasn't changed.",
        });
        // Remove status query param after showing toast
        const p = new URLSearchParams(window.location.search);
        p.delete("status");
        const newSearch = p.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}${window.location.hash}`;
        window.history.replaceState(null, "", newUrl);
      }
    } catch (err) {
      logger.error("Error handling pricing status param:", err as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    try {
      trackEvent('pricing_page_viewed', {
        current_tier: currentTier,
      });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = async (plan: "pro_starter" | "elite_pro" | "ultra_team") => {
    try {
      if (!isAuthenticated || !user?.id) {
        window.location.href = "/login?redirect=/pricing";
        return;
      }
      // Block team members from upgrading
      if (isOnTeam) {
        toast({
          title: "Cannot upgrade while on a team",
          description: `You must leave "${teamName || 'your team'}" before upgrading. Go to Settings to manage your team membership.`,
          variant: "destructive",
        });
        return;
      }
      try {
        trackEvent('checkout_started', {
          requested_plan: plan,
          current_tier: currentTier,
        });
      } catch {}
      const res = await apiRequest("POST", "/api/billing/create-checkout-session", {
        plan,
        userId: user?.id,
        email: user?.email,
      });
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url as string;
      } else {
        alert("Failed to start checkout session.");
      }
    } catch (err: any) {
      logger.error('Checkout session error', err);
      const errorMessage = err?.message || "Unknown error";
      alert(`Error creating checkout session: ${errorMessage}`);
    }
  };

  const handleManageBilling = async () => {
    try {
      if (!isAuthenticated || !user?.id) {
        window.location.href = "/login?redirect=/pricing";
        return;
      }
      const res = await apiRequest("POST", "/api/billing/customer-portal", {
        userId: user?.id,
      });
      const data = await res.json();
      if (data?.url) {
        try {
          trackEvent('billing_portal_opened', {
            current_tier: currentTier,
          });
        } catch {}
        window.location.href = data.url as string;
      } else {
        toast({ title: "Manage Billing", description: data?.message || "Unable to open billing portal." });
      }
    } catch (err) {
      logger.error('Billing portal error', err as any);
      toast({ title: "Manage Billing", description: "Error opening billing portal." });
    }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <SEO
        title="Pricing Plans | BounceBunny"
        description="Choose the perfect training plan for your basketball goals. From free starter plans to elite coaching - find the right fit for your athletic journey."
        canonical="https://bouncebunny.app/pricing"
      />
      {/* Hero */}
      <section className="text-center max-w-3xl mx-auto mb-4 sm:mb-6">
        <h1 data-testid="pricing-hero-title" className="title-1">Choose your plan</h1>
        <p className="title-2">Train smarter and better with personalized plans.</p>
        {/* Removed global current plan badge to avoid confusion; badge now appears above the matching card */}
        <div className="mt-4 flex items-center justify-center">
          {isAuthenticated && (
            <button
              onClick={handleManageBilling}
              className="btn btn-outline"
            >
              Manage Billing
            </button>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section data-testid="pricing-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
        {/* Free Plan */}
        <Card data-testid="pricing-card" className="relative rounded-2xl border-white/10 bg-white/5 backdrop-blur-xl">
          {renderCurrentPlanBadge('free')}
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-xl">🏀 Free</span>
              <span className="text-2xl font-bold">$0<span className="text-sm font-normal text-white/60">/month</span></span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <FeatureItem included>1 personalized training plan</FeatureItem>
              <FeatureItem included>Edit your stats once per month to unlock a new plan</FeatureItem>
              <FeatureItem included>Access to the Spotify playlist</FeatureItem>
            
              <FeatureItem included={false}>Access to experimental tools</FeatureItem>
            </ul>
            <p className="mt-4 text-sm text-white/60">Great for exploring what’s possible before committing.</p>
          </CardContent>
          <CardFooter>
            <Link href="/signup" className="btn bg-white text-black w-full">
              Get started free
            </Link>
          </CardFooter>
        </Card>

        {/* Pro Starter - Featured */}
        <Card data-testid="pricing-card" className="relative rounded-2xl border-white/10 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-xl ring-2 ring-white/30">
          {renderCurrentPlanBadge('pro_starter')}
          {/* Removed static 'Most Popular' badge to prioritize current plan indicator */}
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-xl">🚀 Pro Starter</span>
              <span className="text-2xl font-bold">$4.99<span className="text-sm font-normal text-white/70">/month</span></span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <FeatureItem included>Up to 15 training plans per month</FeatureItem>
                       <FeatureItem included>Consult a professional performance coach and get feedback (up to 5×/month)</FeatureItem>
              <FeatureItem included>Weekly premium content: drills, breakdowns, advanced techniques</FeatureItem>
              <FeatureItem included>Early access to experimental tools</FeatureItem>
            </ul>
            <p className="mt-4 text-sm text-white/70">Perfect for enthusiasts who want flexibility and expert guidance as they train.</p>
          </CardContent>
          <CardFooter className="flex-col">
            <button
              onClick={() => handleUpgrade("pro_starter")}
              className="btn bg-white text-black w-full"
              disabled={currentTier === 'pro_starter' || isOnTeam}
            >
              {currentTier === 'pro_starter' ? "Current Plan" : isOnTeam ? "Leave Team to Upgrade" : (isAuthenticated ? "Upgrade" : "Login to Upgrade")}
            </button>
            {isOnTeam && currentTier !== 'pro_starter' && (
              <p className="text-xs text-orange-400 mt-2 text-center">
                You're on team "{teamName}". Leave your team first.
              </p>
            )}
          </CardFooter>
        </Card>

        {/* Elite Pro */}
        <Card data-testid="pricing-card" className="relative rounded-2xl border-white/10 bg-white/5 backdrop-blur-xl">
          {renderCurrentPlanBadge('elite_pro')}
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-xl">🦅 Elite Pro</span>
              <span className="text-2xl font-bold">$9.99<span className="text-sm font-normal text-white/60">/month</span></span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <FeatureItem included>Up to 40 trainig plans per month</FeatureItem>
              <FeatureItem included>Consult a professional performance coach and get feedback (up to 10×/month)</FeatureItem>
              <FeatureItem included>Weekly premium content: drills, breakdowns, advanced techniques</FeatureItem>
              <FeatureItem included>Early access to experimental tools</FeatureItem>
            </ul>
            <p className="mt-4 text-sm text-white/60">Built for people who are ready to unlock their full potential with expert support.</p>
          </CardContent>
          <CardFooter className="flex-col">
            <button
              onClick={() => handleUpgrade("elite_pro")}
              className="btn bg-white text-black w-full"
              disabled={currentTier === 'elite_pro' || isOnTeam}
            >
              {currentTier === 'elite_pro' ? "Current Plan" : isOnTeam ? "Leave Team to Upgrade" : (isAuthenticated ? "Upgrade" : "Login to Upgrade")}
            </button>
            {isOnTeam && currentTier !== 'elite_pro' && (
              <p className="text-xs text-orange-400 mt-2 text-center">
                You're on team "{teamName}". Leave your team first.
              </p>
            )}
          </CardFooter>
        </Card>

        {/* Ultra Team */}
        <Card data-testid="pricing-card" className="relative rounded-2xl border-white/10 bg-white/5 backdrop-blur-xl">
          {renderCurrentPlanBadge('ultra_team')}
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-xl">⚡ Ultra Team</span>
              <span className="text-2xl font-bold">$19.99<span className="text-sm font-normal text-white/60">/month</span></span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <FeatureItem included>Up to 120 training plans per month</FeatureItem>
              <FeatureItem included>Consult a professional performance coach and get feedback (up to 30×/month)</FeatureItem>
              <FeatureItem included>Weekly premium content: drills, breakdowns, advanced techniques</FeatureItem>
              <FeatureItem included>Early access to experimental tools</FeatureItem>
            </ul>
            <p className="mt-4 text-sm text-white/60">Perfect for teams and serious athletes who demand maximum support and flexibility.</p>
          </CardContent>
          <CardFooter className="flex-col">
            <button
              onClick={() => handleUpgrade("ultra_team")}
              className="btn bg-white text-black w-full"
              disabled={currentTier === 'ultra_team' || isOnTeam}
            >
              {currentTier === 'ultra_team' ? "Current Plan" : isOnTeam ? "Leave Team to Upgrade" : (isAuthenticated ? "Upgrade" : "Login to Upgrade")}
            </button>
            {isOnTeam && currentTier !== 'ultra_team' && (
              <p className="text-xs text-orange-400 mt-2 text-center">
                You're on team "{teamName}". Leave your team first.
              </p>
            )}
          </CardFooter>
        </Card>
      </section>

      {/* Referral Section */}
      <section className="mt-10 sm:mt-16">
        <Card className="rounded-2xl border-white/10 bg-white/5 backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl">🎁 Need more plan edits? Invite your crew.</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-white/80">Unlock extra edits just by sharing the app.</p>
                <ul className="mt-3 space-y-2 text-white/80">
                  <li>👥 Invite 2 friends → Get 3 extra credits</li>
                  <li>👥 Invite 5 friends → Get 10 extra credits</li>
                  <li>👥 Invite 8 friends → Get 18 extra credits</li>
                </ul>
                <p className="mt-3 text-sm text-white/60">Each edit gives you a new custom plan based on your current stats. More edits = faster progress.</p>
              </div>
              <div className="h-full flex items-center md:justify-end">
                <Link href="/my-credits" className="btn btn-outline">
                  View referral details
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}